import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import {
  SubmitButton,
  Input,
  Select,
  Checkbox,
  Form as FormFormik,
} from 'formik-semantic-ui-react';
import { Form as FormSemantic } from 'semantic-ui-react';
import * as Yup from 'yup';
import axios from 'axios';
import clsx from 'clsx';

import backgroundDesktop from './assets/hmx_pos_pitts2_sweeps_form_header.jpg';
import backgroundMobile from './assets/hmx_pos_pitts2_sweeps_form_header.jpg';
import backgroundGraphicDesktop from './assets/hmx_pos_pitts2_sweeps_form_header.jpg';
import backgroundGraphicMobile from './assets/hmx_pos_pitts2_sweeps_form_header.jpg';

import './Form.scss';
import 'semantic-ui-css/semantic.min.css';

type ValuesType = {
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  other_company?: string;
  supervisor: string;
  city: string;
  state: string;
  legal: boolean;
};

type ErrorValuesType = {
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
  supervisor?: string;
  city?: string;
  state?: string;
};

type ValueNamesType = (
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'company'
  | 'supervisor'
  | 'city'
  | 'state'
)[];

const URL = 'https://api.experiencehbo.com/submit';
const FORM_ID = 'world-of-westeros-2026-1pd';
const isBrowser = typeof window !== 'undefined';
const POINT_OF_ENTRY_OVERLAY = 'overlay';
const POINT_OF_ENTRY_PAGE = 'page';
const IN_EXPERIENCE_BODY_CLASS_NAME = 'in-experience';
const PRELOAD_QUERY_PARAM_NAME = 'preload';

const getInExperienceFromLocation = (): boolean => {
  if (!isBrowser) return false;

  const searchParams = new URLSearchParams(window.location.search);

  return searchParams.get('inExperience') === 'true';
};

const getPreloadFromLocation = (): boolean => {
  if (!isBrowser) return false;

  const searchParams = new URLSearchParams(window.location.search);

  return searchParams.get(PRELOAD_QUERY_PARAM_NAME) === 'true';
};

const SignupSchema = Yup.object().shape({
  first_name: Yup.string()
    .min(2, 'Too Short!')
    .max(20, 'Too Long!')
    .required('Required'),
  last_name: Yup.string()
    .min(2, 'Too Short!')
    .max(20, 'Too Long!')
    .required('Required'),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Required'),
  company: Yup.string()
    .matches(
      /^(?!AT&T$).+$/,
      'Sorry, you’re not eligible to participate. See Official Rules for details.'
    )
    .required(),

  other_company: Yup.string().when('company', {
    is: 'Other',
    then: (schema) =>
      schema.min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  supervisor: Yup.string()
    .min(2, 'Too Short!')
    .max(20, 'Too Long!')
    .required('Required'),
  city: Yup.string()
    .min(2, 'Too Short!')
    .max(20, 'Too Long!')
    .required('Required'),
  state: Yup.string().required('Required'),
  legal: Yup.boolean().not([false]),
});

const Form = () => {
  const [isFormComplete, setFormComplete] = useState(false);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [overLimitError, setOverLimitError] = useState<string | null>(null);
  const [isInExperience] = useState<boolean>(getInExperienceFromLocation);
  const [isPreload] = useState<boolean>(getPreloadFromLocation);
  const isExperienceOverlay = isInExperience;

  const [selectedCompany, setCompany] = useState<null | string>(null);
  const [selectedState, setState] = useState<null | string>(null);

  useEffect(() => {
    document.body.classList.toggle(
      IN_EXPERIENCE_BODY_CLASS_NAME,
      isInExperience
    );

    return () => {
      document.body.classList.remove(IN_EXPERIENCE_BODY_CLASS_NAME);
    };
  }, [isInExperience]);

  const initialValues: ValuesType = {
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    other_company: '',
    supervisor: '',
    city: '',
    state: '',
    legal: false,
  };

  const companyNames = [
    {
      key: 'altice',
      text: 'Altice/Optimum/Suddenlink',
      value: 'Altice/Optimum/Suddenlink',
    },
    { key: 'astound', text: 'Astound', value: 'Astound' },
    { key: 'att', text: 'AT&T', value: 'AT&T' },
    { key: 'cox', text: 'Cox', value: 'Cox' },
    { key: 'cricket', text: 'Cricket', value: 'Cricket' },
    { key: 'directTV', text: 'DIRECTV', value: 'DirectTV' },
    { key: 'dish', text: 'DISH', value: 'DISH' },
    { key: 'frontier', text: 'Frontier', value: 'Frontier' },
    { key: 'hulu', text: 'Hulu', value: 'Hulu' },
    { key: 'mediacom', text: 'Mediacom', value: 'Mediacom' },
    { key: 'nctc', text: 'NCTC', value: 'NCTC' },
    { key: 'spectrum', text: 'Spectrum', value: 'Spectrum' },
    { key: 'verizon', text: 'Verizon', value: 'Verizon' },
    { key: 'wow!', text: 'WOW!', value: 'WOW!' },
    { key: 'xfinity', text: 'Xfinity', value: 'Xfinity' },
    { key: 'other', text: 'OTHER', value: 'Other' },
  ];
  const states = [
    { key: 'Alabama (AL)', text: 'Alabama', value: 'AL' },
    { key: 'Alaska (AK)', text: 'Alaska', value: 'AK' },
    { key: 'Arizona (AZ)', text: 'Arizona', value: 'AZ' },
    { key: 'Arkansas (AR)', text: 'Arkansas', value: 'AR' },
    { key: 'California (CA)', text: 'California', value: 'CA' },
    { key: 'Colorado (CO)', text: 'Colorado', value: 'CO' },
    { key: 'Connecticut (CT)', text: 'Connecticut', value: 'CT' },
    { key: 'Delaware (DE)', text: 'Delaware', value: 'DE' },
    {
      key: 'District Of Columbia (DC)',
      text: 'District Of Columbia',
      value: 'DC',
    },
    { key: 'Florida (FL)', text: 'Florida', value: 'FL' },
    { key: 'Georgia (GA)', text: 'Georgia', value: 'GA' },
    { key: 'Hawaii (HI)', text: 'Hawaii', value: 'HI' },
    { key: 'Idaho (ID)', text: 'Idaho', value: 'ID' },
    { key: 'Illinois (IL)', text: 'Illinois', value: 'IL' },
    { key: 'Indiana (IN)', text: 'Indiana', value: 'IN' },
    { key: 'Iowa (IA)', text: 'Iowa', value: 'IA' },
    { key: 'Kansas (KS)', text: 'Kansas', value: 'KS' },
    { key: 'Kentucky (KY)', text: 'Kentucky', value: 'KY' },
    { key: 'Louisiana (LA)', text: 'Louisiana', value: 'LA' },
    { key: 'Maine (ME)', text: 'Maine', value: 'ME' },
    { key: 'Maryland (MD)', text: 'Maryland', value: 'MD' },
    { key: 'Massachusetts (MA)', text: 'Massachusetts', value: 'MA' },
    { key: 'Michigan (MI)', text: 'Michigan', value: 'MI' },
    { key: 'Minnesota (MN)', text: 'Minnesota', value: 'MN' },
    { key: 'Mississippi (MS)', text: 'Mississippi', value: 'MS' },
    { key: 'Missouri (MO)', text: 'Missouri', value: 'MO' },
    { key: 'Montana (MT)', text: 'Montana', value: 'MT' },
    { key: 'Nebraska (NE)', text: 'Nebraska', value: 'NE' },
    { key: 'Nevada (NV)', text: 'Nevada', value: 'NV' },
    { key: 'New Hampshire (NH)', text: 'New Hampshire', value: 'NH' },
    { key: 'New Jersey (NJ)', text: 'New Jersey', value: 'NJ' },
    { key: 'New Mexico (NM)', text: 'New Mexico', value: 'NM' },
    { key: 'New York (NY)', text: 'New York', value: 'NY' },
    { key: 'North Carolina (NC)', text: 'North Carolina', value: 'NC' },
    { key: 'North Dakota (ND)', text: 'North Dakota', value: 'ND' },
    { key: 'Ohio (OH)', text: 'Ohio', value: 'OH' },
    { key: 'Oklahoma (OK)', text: 'Oklahoma', value: 'OK' },
    { key: 'Oregon (OR)', text: 'Oregon', value: 'OR' },
    { key: 'Pennsylvania (PA)', text: 'Pennsylvania', value: 'PA' },
    { key: 'Rhode Island (RI)', text: 'Rhode Island', value: 'RI' },
    { key: 'South Carolina (SC)', text: 'South Carolina', value: 'SC' },
    { key: 'South Dakota (SD)', text: 'South Dakota', value: 'SD' },
    { key: 'Tennessee (TN)', text: 'Tennessee', value: 'TN' },
    { key: 'Texas (TX)', text: 'Texas', value: 'TX' },
    { key: 'Utah (UT)', text: 'Utah', value: 'UT' },
    { key: 'Vermont', text: 'Vermont', value: 'VT' },
    { key: 'Virginia', text: 'Virginia', value: 'VA' },
    { key: 'Washington', text: 'Washington', value: 'WA' },
    { key: 'West Virginia', text: 'West Virginia', value: 'WV' },
    { key: 'Wisconsin', text: 'Wisconsin', value: 'WI' },
    { key: 'Wyoming', text: 'Wyoming', value: 'WY' },
    { key: 'Other', text: 'Other', value: 'Other' },
  ];

  // ? GA_TAG_EVENT
  useEffect(() => {
    if (isPreload) return;

    const paramsString = window.location.search;
    const searchParams = new URLSearchParams(paramsString);
    const pointOfEntry =
      searchParams.get('inExperience') === 'true'
        ? POINT_OF_ENTRY_OVERLAY
        : POINT_OF_ENTRY_PAGE;

    if (isBrowser)
      window.dataLayer?.push({
        event: 'sweepsFormStart',
        pointOfEntry,
      });
  }, [isPreload]);
  useEffect(() => {
    const paramsString = window.location.search;
    const searchParams = new URLSearchParams(paramsString);
    const pointOfEntry =
      searchParams.get('inExperience') === 'true'
        ? POINT_OF_ENTRY_OVERLAY
        : POINT_OF_ENTRY_PAGE;

    if (isFormComplete && isBrowser)
      window.dataLayer?.push({
        event: 'sweepsFormComplete',

        company: selectedCompany,
        state: selectedState,
        pointOfEntry,
      });
  }, [isFormComplete]);

  const handleSubmit = (
    values: ValuesType,
    { setSubmitting, setErrors }: FormikHelpers<ValuesType>
  ) => {
    setOverLimitError('');

    const { legal: _, company, other_company, ...rest } = values;

    const standardValues = {
      ...rest,
      company: company === 'Other' ? other_company : company,
    };
    const formData = {
      form_id: FORM_ID,
      ...standardValues,
    };

    axios
      .post(URL, JSON.stringify(formData))
      .then((response) => {
        const errors: ErrorValuesType = {};
        response.data.errors.forEach(
          ({ message, path }: { message: string; path: ValueNamesType }) => {
            if (!path) return;
            errors[path[0]] = message;
          }
        );

        setOverLimitError(
          response.data.errors.some(
            ({ type }: { type: string }) => type === 'over-limit-error'
          )
        );

        setErrors(errors);

        if (!Object.keys(errors).length && !response.data.errors.length) {
          setFormComplete(true);

          setCompany(values.company);
          setState(values.state);
        }

        setSubmitting(false);
      })
      .catch((e) => {
        Sentry.captureException(e);
        // eslint-disable-next-line no-console
        console.error({ e });
        setResponseError(
          `Error submitting. Please check your connection [${e.code}]`
        );
        setSubmitting(false);
      });
  };

  const handleClick = () => {
    setResponseError('');
    setOverLimitError('');
  };

  return (
    <div id="form">
      {isInExperience != null && !isInExperience && !isFormComplete ? (
        <picture>
          <source srcSet={backgroundGraphicMobile} media="(max-width: 768px)" />
          <img
            className={clsx(isFormComplete && 'form-complete', 'background')}
            src={backgroundGraphicDesktop}
            alt=""
          />
        </picture>
      ) : 1 < 0 ? (
        <picture>
          <source srcSet={backgroundMobile} media="(max-width: 768px)" />
          <img
            className={'experience-background'}
            src={backgroundDesktop}
            alt=""
          />
        </picture>
      ) : (
        <></>
      )}

      {isFormComplete ? (
        <div className="form-complete-container">
          <div>
            <p className={clsx('form-complete-heading')}>
              Thanks for entering! Good luck!
            </p>

            <p className={clsx('form-complete-text')}>
              Come back to enter once a day, every day until 11:59PM ET on
              7/6/26, to increase your chances of winning.
            </p>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={SignupSchema}
          onSubmit={handleSubmit}>
          {({ errors, touched, isSubmitting, values }) => (
            <FormFormik
              noValidate
              className={clsx(isExperienceOverlay && 'form--overlay')}>
              {true && (
                <h1 className="form-heading">
                  HBO MAX A SONG OF ICE AND FIRE SWEEPSTAKES
                </h1>
              )}

              <FormSemantic.Group widths="equal">
                <Input
                  required
                  errorPrompt
                  id="form-input-control-first-name"
                  name="first_name"
                  label="First Name"
                  placeholder={isExperienceOverlay ? 'Enter Name' : ''}
                  disabled={isSubmitting}
                />
                <Input
                  required
                  errorPrompt
                  id="form-input-control-last-name"
                  name="last_name"
                  label="Last Name"
                  placeholder={isExperienceOverlay ? 'Enter Last Name' : ''}
                  disabled={isSubmitting}
                />
              </FormSemantic.Group>
              <FormSemantic.Group widths="equal">
                <Input
                  required
                  errorPrompt
                  id="form-input-control-city"
                  name="city"
                  label="City"
                  placeholder={isExperienceOverlay ? 'Enter City' : ''}
                  disabled={isSubmitting}
                />
                <Select
                  required
                  fluid
                  errorPrompt
                  id="form-input-control-state"
                  name="state"
                  label="State"
                  placeholder={isExperienceOverlay ? 'Select State' : ''}
                  options={states}
                  disabled={isSubmitting}
                />
              </FormSemantic.Group>
              <FormSemantic.Group widths="equal">
                <Input
                  required
                  errorPrompt
                  id="form-input-control-work-email"
                  name="email"
                  label="Work Email"
                  placeholder={isExperienceOverlay ? 'Enter Work Email' : ''}
                  type="email"
                  disabled={isSubmitting}
                />
                <Input
                  required
                  errorPrompt
                  id="form-input-control-supervisor-name"
                  name="supervisor"
                  label="Supervisor Name"
                  placeholder={
                    isExperienceOverlay ? 'Enter Supervisor Name' : ''
                  }
                  disabled={isSubmitting}
                />
              </FormSemantic.Group>
              <Select
                required
                fluid
                errorPrompt
                id="form-input-control-company-name"
                name="company"
                label="Company Name"
                placeholder={isExperienceOverlay ? 'Select Company Name' : ''}
                options={companyNames}
                disabled={isSubmitting}
              />

              {values.company === 'Other' && (
                <Input
                  required
                  errorPrompt
                  id="form-input-control-other-company-name"
                  name="other_company"
                  label="Company Name"
                  placeholder="Please Specify"
                  disabled={isSubmitting}
                />
              )}
              <Checkbox
                required
                id="form-input-control-checkbox"
                name="legal"
                disabled={isSubmitting}
              />
              <div
                className={`form-legal${
                  errors.legal && touched.legal ? ' error' : ''
                }`}>
                I agree to the{' '}
                <a href="rules.html" target="_blank" rel="noreferrer">
                  Official Rules
                </a>
              </div>
              <div className="legal-information">
                NO PURCHASE NECESSARY. Open to legal residents of the 50 US and
                D.C., 18 years of age or older. Sweepstakes ends 11:59PM ET on
                7/6/26. Void where prohibited. See{' '}
                <a href="rules.html" target="_blank" rel="noreferrer">
                  Official Rules
                </a>{' '}
                for details including employment/contractor requirements.
              </div>
              {responseError || Object.keys(errors).length || overLimitError ? (
                <p className={clsx('error-messages')}>
                  {Object.keys(errors).length ? (
                    'There are items that require your attention'
                  ) : overLimitError ? (
                    <span>
                      Sorry, you have already entered today. See{' '}
                      <a href="rules.html" target="_blank" rel="noreferrer">
                        Official Rules
                      </a>{' '}
                      for details.
                    </span>
                  ) : (
                    responseError
                  )}
                </p>
              ) : (
                <></>
              )}
              <SubmitButton onClick={() => handleClick()}>ENTER</SubmitButton>
            </FormFormik>
          )}
        </Formik>
      )}
    </div>
  );
};

export default Form;
